import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, CornerDownLeft, Home, Receipt, ArrowUpRight, ArrowDownLeft, Plus, Banknote, CreditCard, Coins, PiggyBank, RefreshCw, Repeat, Split, Users, BarChart3, Gift, Landmark, ShieldCheck, User, Lock, Bell, Sparkles } from "lucide-react";

const COMMANDS = [
  { label: "Home", to: "/app", icon: Home, kw: "dashboard" },
  { label: "Send money", to: "/app/send", icon: ArrowUpRight, kw: "pay transfer" },
  { label: "Receive", to: "/app/receive", icon: ArrowDownLeft, kw: "qr request" },
  { label: "Add money", to: "/app/add-money", icon: Plus, kw: "deposit top up" },
  { label: "Withdraw", to: "/app/withdraw", icon: Banknote, kw: "cash out bank" },
  { label: "Activity", to: "/app/activity", icon: Receipt, kw: "history transactions" },
  { label: "Cards", to: "/app/cards", icon: CreditCard, kw: "virtual physical debit" },
  { label: "Crypto", to: "/app/crypto", icon: Coins, kw: "bitcoin" },
  { label: "Savings", to: "/app/savings", icon: PiggyBank, kw: "goals" },
  { label: "Convert (USD/HTG)", to: "/app/converter", icon: RefreshCw, kw: "currency exchange" },
  { label: "Recurring transfers", to: "/app/recurring", icon: Repeat, kw: "auto schedule" },
  { label: "Split a bill", to: "/app/split", icon: Split, kw: "share" },
  { label: "Jack Send", to: "/app/jacksend", icon: Users, kw: "phone email" },
  { label: "Requests", to: "/app/requests", icon: ArrowDownLeft, kw: "money request" },
  { label: "Spending insights", to: "/app/insights", icon: BarChart3, kw: "charts analytics" },
  { label: "Referrals", to: "/app/referral", icon: Gift, kw: "invite earn" },
  { label: "Business banking", to: "/app/banking", icon: Landmark, kw: "treasury account routing" },
  { label: "Account Guard", to: "/app/guard", icon: ShieldCheck, kw: "security ai fraud monitor" },
  { label: "Profile", to: "/app/profile", icon: User, kw: "account me" },
  { label: "Security settings", to: "/app/security", icon: Lock, kw: "2fa pin devices" },
  { label: "Notifications", to: "/app/notifications", icon: Bell, kw: "alerts" },
  { label: "Assistant / Support", to: "/app/assistant", icon: Sparkles, kw: "help human chat" },
];

export function CommandBar() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((o) => !o); setQ(""); setIdx(0); }
      if (e.key === "Escape") setOpen(false);
    };
    const onOpen = () => { setOpen(true); setQ(""); setIdx(0); };
    window.addEventListener("keydown", onKey);
    window.addEventListener("open-command-bar", onOpen);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("open-command-bar", onOpen); };
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return COMMANDS;
    return COMMANDS.filter((c) => c.label.toLowerCase().includes(s) || (c.kw || "").includes(s));
  }, [q]);

  const go = (to) => { setOpen(false); setQ(""); nav(to); };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center pt-[12vh] px-4" data-testid="command-bar">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="relative w-full max-w-xl bg-background rounded-2xl border border-border shadow-2xl overflow-hidden">
        <div className="flex items-center gap-3 px-4 border-b border-border">
          <Search size={18} className="text-muted-foreground shrink-0" />
          <input autoFocus data-testid="command-input" value={q}
            onChange={(e) => { setQ(e.target.value); setIdx(0); }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(i + 1, filtered.length - 1)); }
              if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
              if (e.key === "Enter" && filtered[idx]) go(filtered[idx].to);
            }}
            placeholder="Search actions and pages…"
            className="flex-1 bg-transparent outline-none h-14 text-base" />
          <kbd className="text-[10px] font-semibold text-muted-foreground bg-secondary px-1.5 py-0.5 rounded">ESC</kbd>
        </div>
        <div className="max-h-[50vh] overflow-y-auto py-2">
          {filtered.length === 0 && <div className="px-4 py-6 text-center text-sm text-muted-foreground">No results</div>}
          {filtered.map((c, i) => (
            <button key={c.to} data-testid={`command-item-${i}`} onClick={() => go(c.to)}
              onMouseEnter={() => setIdx(i)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm ${i === idx ? "bg-primary/10 text-primary" : "text-foreground"}`}>
              <c.icon size={17} className={i === idx ? "text-primary" : "text-muted-foreground"} />
              <span className="flex-1">{c.label}</span>
              {i === idx && <CornerDownLeft size={14} className="text-primary" />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
